import { createHookContext } from '@clover-util/react-context';
import { BYOStep, BYOStepId, BYOVariants } from 'api/contentful/contentTypes.cloverdotcom';
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { BuildYourOwnModuleProps, BYOStepOrder } from '../BuildYourOwnModule';
import { BYOBusinessOffering, BYOSelectionState, useBYOSelections } from './sub-hooks/useBYOSelections';
import { tryAddBYOSystemToCart } from './utils';
import { BYO_SELECTION_STATE_KEY } from './sub-hooks/utils';
import { useDCSProductsQuery } from 'hooks/DCS/useDCSProductsQuery';
import { useTranslation } from 'react-i18next';
import { SessionStorage } from 'utils/SessionStorage';
import { useCartContext } from 'components/Cart/hooks/useCartContext';

export type CurrentStepData = BYOStep & { nextStepId: BYOStepId; isImageClickable: boolean };

export const { useBuildYourOwn, BuildYourOwnProvider } = createHookContext('BuildYourOwn', () => {
  const initialByoMount = !SessionStorage.tryGetJSONItem(BYO_SELECTION_STATE_KEY);
  const [isInitialByoMount, setIsInitialByoMount] = useState(initialByoMount);
  const [currentStepId, setCurrentStepId] = useState(BYOStepOrder[0]);
  const [byoData, setByoData] = useState<BuildYourOwnModuleProps>({});
  const { search, pathname } = useLocation();
  const history = useHistory();
  const { addProductsWithSoftwarePlan } = useCartContext();
  const { data: dcsProducts = [] } = useDCSProductsQuery();

  const { t } = useTranslation();
  const isValidStep = (stepId?: BYOStepId) => stepId && BYOStepOrder.includes(stepId);

  const { steps, variant = BYOVariants.STANDALONE } = byoData;
  const isStandAloneVariant = variant === BYOVariants.STANDALONE;
  const isByoRoute = pathname.includes('/build-your-own');

  const currentStepData: CurrentStepData | undefined = useMemo(() => {
    if (!steps) return;

    const validStepId = isValidStep(currentStepId) ? currentStepId : BYOStepOrder[0];
    const stepIndex = BYOStepOrder.indexOf(validStepId);
    const nextStepId = BYOStepOrder[stepIndex + 1];

    const step = steps.find(({ queryValue }) => queryValue === validStepId);
    const isImageClickable = false;

    return step ? { ...step, nextStepId, isImageClickable } : undefined;
  }, [steps, currentStepId]);

  const retailOrServicesItem = steps?.[0]?.items?.find(
    ({ itemKey }) => itemKey === BYOBusinessOffering.RETAIL_OR_SERVICES,
  );
  const defaultSupportedSoftwarePlanDcsIds =
    retailOrServicesItem?.supportedSoftwarePlans?.map(({ dcsId }) => dcsId as string) || [];

  const {
    tryAddSoftwarePlanFromQueryParam,
    selectedSoftwarePlanAdditionalMonthlyFee,
    filteredItemsForStep,
    addAllCompatibleAccessories,
    isStepCompleted,
    ...byoSelections
  } = useBYOSelections(defaultSupportedSoftwarePlanDcsIds, currentStepData, steps?.[1]);

  const hydratedSubHeadlineWithSoftwarePlanAdditionalDeviceFee = useMemo(() => {
    if (currentStepId !== BYOStepId.DEVICES) return currentStepData?.subHeadline || '';

    if (!currentStepData?.subHeadline || selectedSoftwarePlanAdditionalMonthlyFee === undefined) return '';

    const formattedSoftwarePlanAdditionalDeviceFee = t('build-your-own.pricing.upfront', {
      price: selectedSoftwarePlanAdditionalMonthlyFee,
    });

    return currentStepData.subHeadline.replace(
      '{{SOFTWARE_PLAN_ADDITIONAL_DEVICE_FEE}}',
      formattedSoftwarePlanAdditionalDeviceFee,
    );
  }, [currentStepData?.subHeadline, currentStepId, selectedSoftwarePlanAdditionalMonthlyFee, t]);

  useEffect(() => {
    if (!isStandAloneVariant || !steps || !search) return;

    const params = new URLSearchParams(search);
    const paramStepId = params.get('step') as BYOStepId;
    const step = steps.find(({ queryValue }) => queryValue === paramStepId);

    step?.queryValue && setCurrentStepId(step.queryValue);
  }, [isStandAloneVariant, search, steps]);

  const moveToStep = (stepId?: BYOStepId, shouldReplace = false) => {
    if (!stepId) return;

    setCurrentStepId(stepId);

    if (!isStandAloneVariant) return;

    const urlParams = { search: `?step=${stepId}` };
    if (shouldReplace) {
      history.replace(urlParams);
    } else {
      history.push(urlParams);
    }
  };

  const tryPopulateBasedOnURL = useCallback(() => {
    if (!isByoRoute || !steps) return;

    const params = new URLSearchParams(search);
    const paramStepId = params.get('step') as BYOStepId;
    const paramSoftwarePlan = params.get('softwarePlan');

    const isSoftwarePlanAdded = tryAddSoftwarePlanFromQueryParam(paramSoftwarePlan);

    if (isSoftwarePlanAdded) {
      setIsInitialByoMount(false);
      moveToStep(BYOStepId.DEVICES, true);
      return;
    }

    if (isInitialByoMount || !isValidStep(paramStepId)) {
      setIsInitialByoMount(false);
      moveToStep(BYOStepOrder[0], true);
    }

    const prevStepId = currentStepId
      ? BYOStepOrder[BYOStepOrder.indexOf(currentStepId) - 1]
      : BYOStepId.BUSINESS_OFFERING;
    if (!isStepCompleted(prevStepId)) {
      moveToStep(prevStepId, true);
    }
  }, [isByoRoute, steps, tryAddSoftwarePlanFromQueryParam, isInitialByoMount]);

  const handleNextClick = (event: MouseEvent, selectionState: BYOSelectionState) => {
    if (currentStepId === BYOStepId.ACCESSORIES) {
      tryAddBYOSystemToCart(selectionState, addProductsWithSoftwarePlan, dcsProducts);
    }

    currentStepData?.nextStepId && moveToStep(currentStepData.nextStepId);
    event.stopPropagation();
  };

  return {
    isInitialByoMount,
    setIsInitialByoMount,
    tryPopulateBasedOnURL,
    setByoData,
    moveToStep,
    currentStepData,
    currentStepId,
    filteredItemsForStep,
    handleNextClick,
    isStepCompleted,
    hydratedSubHeadlineWithSoftwarePlanAdditionalDeviceFee,
    addAllCompatibleAccessories,
    ...byoData,
    ...byoSelections,
  };
});
